import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, CheckCircle2, XCircle, Copy, ExternalLink, FlaskConical,
  CreditCard, Radio, AlertTriangle, RefreshCw,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Check = ({ ok, label, hint }) => (
  <div className="flex items-start gap-2.5" data-testid={`eo-check-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
    {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
        : <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />}
    <div>
      <p className={`text-sm font-semibold ${ok ? 'text-gray-900' : 'text-red-700'}`}>{label}</p>
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  </div>
);

const EveryorgSetupView = () => {
  const [conn, setConn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [test, setTest] = useState(null);
  const [testStatus, setTestStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [armed, setArmed] = useState(false);
  const [togglingDonations, setTogglingDonations] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/everyorg/connection`, { withCredentials: true });
      setConn(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not read the Every.org connection');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!test?.donation_id || testStatus?.webhook_received) return;
    const poll = setInterval(async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/everyorg/test-donation/${test.donation_id}`,
          { withCredentials: true });
        setTestStatus(res.data);
        if (res.data.webhook_received) { clearInterval(poll); load(); }
      } catch (e) { /* keep polling while the donation is completed on Every.org */ }
    }, 4000);
    const stop = setTimeout(() => clearInterval(poll), 600000);
    return () => { clearInterval(poll); clearTimeout(stop); };
  }, [test, testStatus, load]);

  const startTest = async () => {
    if (!conn?.is_staging && !armed) return;
    setStarting(true);
    setError('');
    setTestStatus(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/everyorg/test-donation`,
        { amount: 5, return_origin: window.location.origin },
        { withCredentials: true });
      setTest(res.data);
      setArmed(false);
      window.open(res.data.donate_url, '_blank', 'noopener');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not start the test donation');
    } finally {
      setStarting(false);
    }
  };

  const toggleDonations = async (open) => {
    setTogglingDonations(true);
    try {
      await axios.put(`${BACKEND_URL}/api/platform/donations-open`, { open }, { withCredentials: true });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not change the donation switch');
    } finally {
      setTogglingDonations(false);
    }
  };

  const copyWebhook = () => {
    navigator.clipboard.writeText(conn.webhook_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-blue-500 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="everyorg-setup-view">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Every.org Rail</h1>
          <p className="text-sm text-gray-500 mt-1">
            Altrunic's own Every.org partner account — the rail every customer's donations ride on.
            Internal only: this page shows our webhook secret.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1.5 rounded-full text-xs font-black tracking-wider uppercase ${
            conn?.is_staging ? 'bg-amber-100 text-amber-800' : 'bg-red-600 text-white'
          }`} data-testid="eo-env-badge">
            {conn?.is_staging ? 'Staging (test money)' : 'Live (real money)'}
          </span>
          <button onClick={load} className="p-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50">
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-xs text-red-800" data-testid="eo-error">{error}</div>}

      {conn?.is_staging && !conn?.ready_for_dry_run && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5" data-testid="eo-setup-steps">
          <p className="text-sm font-bold text-amber-900">Finish the staging setup to run the dry run</p>
          <ol className="mt-3 space-y-2">
            {(conn.setup_steps || []).map((s, i) => (
              <li key={i} className="flex items-start gap-2.5" data-testid={`eo-setup-step-${i}`}>
                {s.done ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                        : <span className="w-4 h-4 rounded-full border-2 border-amber-400 mt-0.5 flex-shrink-0" />}
                <div>
                  <p className={`text-sm font-semibold ${s.done ? 'text-gray-500 line-through' : 'text-amber-900'}`}>{s.step}</p>
                  <p className="text-xs text-amber-800/80">{s.detail}</p>
                </div>
              </li>
            ))}
          </ol>
          <p className="text-xs text-amber-900 mt-3">
            The token goes in <span className="font-mono font-bold">{conn.webhook_token_env_var}</span> — this is
            Altrunic's own staging account, not a customer's.
          </p>
        </div>
      )}

      {!conn?.is_staging && (
        <div className="bg-slate-900 border border-slate-700 rounded-2xl p-5 flex items-start justify-between gap-6 flex-wrap"
          data-testid="eo-donations-switch">
          <div>
            <p className="text-sm font-bold text-white">
              Public donations are {conn?.donations_open ? 'OPEN' : 'CLOSED'}
            </p>
            <p className="text-xs text-slate-400 mt-1 max-w-xl leading-relaxed">
              {conn?.donations_open
                ? 'Any visitor or employee can make a real donation right now. Close this if you are still verifying the rail.'
                : 'Visitors and employees get "donations aren\'t open yet" instead of a checkout, so the only real charge is your own test. Open it once the rail is proven.'}
            </p>
          </div>
          <button onClick={() => toggleDonations(!conn?.donations_open)} disabled={togglingDonations}
            data-testid="eo-toggle-donations-btn"
            className={`px-4 py-2.5 rounded-xl text-xs font-bold disabled:opacity-50 ${
              conn?.donations_open ? 'bg-slate-700 text-white hover:bg-slate-600'
                                   : 'bg-emerald-600 text-white hover:bg-emerald-700'
            }`}>
            {togglingDonations ? 'Saving…' : conn?.donations_open ? 'Close public donations' : 'Open public donations'}
          </button>
        </div>
      )}

      {conn?.domain_mismatch && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start gap-3" data-testid="eo-domain-mismatch">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-red-900 leading-relaxed">
            <p className="font-bold">The callback is registered against a different domain than you're using.</p>
            <p className="mt-1">
              Registered: <span className="font-mono">{conn.registered_domain}</span> · Running on:{' '}
              <span className="font-mono">{conn.running_domain}</span>. Every.org allows one callback URL per
              account, so update FRONTEND_ORIGIN and re-register the URL below before expecting deliveries here.
            </p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-3">
          <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-4">Configuration</h3>
          <Check ok={conn?.public_key_working} label="Partner API key"
            hint={conn?.public_key_working ? 'Verified against a live nonprofit lookup' : 'EVERYORG_PUBLIC_KEY missing or rejected'} />
          <Check ok={conn?.webhook_token_configured} label="Webhook token"
            hint={conn?.webhook_token_env_var} />
          <Check ok={conn?.route_secret_configured} label="Webhook route secret"
            hint="The secret in the webhook URL — this is what authenticates Every.org" />
          <Check ok={conn?.proven} label="Every.org has called us"
            hint={conn?.proven
              ? `${conn.deliveries_from_everyorg} genuine delivery(ies) received`
              : `No genuine delivery ever — ${conn?.deliveries_simulated || 0} synthetic row(s) from our own tooling don't count`} />
          <div className="pt-3 border-t border-gray-100 text-xs text-gray-500 space-y-1">
            <p>Donate host: <span className="font-mono text-gray-700">{conn?.donate_host}</span></p>
            <p>API base: <span className="font-mono text-gray-700">{conn?.api_base}</span></p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-4">Webhook to register</h3>
          <div className="bg-gray-900 rounded-xl p-3 flex items-center gap-2">
            <code className="text-[11px] text-emerald-300 break-all flex-1" data-testid="eo-webhook-url">{conn?.webhook_url}</code>
            <button onClick={copyWebhook} data-testid="eo-copy-webhook-btn"
              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg text-white flex-shrink-0">
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
          {copied && <p className="text-xs text-emerald-600 font-semibold mt-2">Copied</p>}
          <a href={conn?.register_webhook_at} target="_blank" rel="noreferrer"
            data-testid="eo-register-link"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-800">
            Open the {conn?.is_staging ? 'staging' : 'live'} developer dashboard <ExternalLink className="w-3.5 h-3.5" />
          </a>
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-900 leading-relaxed">
              Every.org allows one webhook URL per account, so pointing it at production stops preview receiving
              deliveries. Staging is a separate account with its own URL, token and test money.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">Dry run</h3>
            <p className="text-xs text-gray-500 mt-1 max-w-2xl leading-relaxed">
              {conn?.is_staging
                ? 'Staging mode: pay with the test card below. No real money moves, but the full callback loop is exercised exactly as it will be in production.'
                : 'Live mode: this is the real rail. A $5 charge on your own card, to a neutral nonprofit, proving the whole chain — charge, callback, confirmation — with a real charge reference.'}
            </p>
          </div>
          <button onClick={startTest} disabled={starting || (!conn?.is_staging && !armed)} data-testid="eo-start-test-btn"
            className={`px-5 py-2.5 rounded-xl text-sm font-bold text-white flex items-center gap-2 disabled:opacity-40 ${
              conn?.is_staging ? 'bg-gradient-to-r from-amber-500 to-orange-600' : 'bg-gradient-to-r from-red-600 to-rose-700'
            }`}>
            {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
            {conn?.is_staging ? 'Start $5 test donation' : 'Charge my card $5 now'}
          </button>
        </div>

        {!conn?.is_staging && (
          <div className="mt-4 bg-red-50 border-2 border-red-300 rounded-xl p-4" data-testid="eo-live-warning">
            <p className="text-sm font-black text-red-900 uppercase tracking-wide">Real money</p>
            <ul className="mt-2 space-y-1 text-xs text-red-900">
              <li>· Your card will be charged <strong>$5.00</strong> immediately.</li>
              <li>· It goes to <strong>GiveDirectly</strong> as a genuine charitable gift and <strong>cannot be refunded</strong>.</li>
              <li>· Every.org will email you a real tax receipt for it.</li>
              <li>· It is flagged as a connection test and stays out of every customer dashboard, report and total.</li>
            </ul>
            <label className="mt-3 flex items-center gap-2 cursor-pointer" data-testid="eo-arm-checkbox-label">
              <input type="checkbox" checked={armed} onChange={e => setArmed(e.target.checked)}
                data-testid="eo-arm-checkbox" className="w-4 h-4 accent-red-600" />
              <span className="text-xs font-bold text-red-900">
                I understand this charges a real card $5 and cannot be undone
              </span>
            </label>
          </div>
        )}

        {conn?.test_card && (
          <div className="mt-4 grid sm:grid-cols-4 gap-3" data-testid="eo-test-card">
            {[['Card', conn.test_card.number], ['Expiry', conn.test_card.expiry],
              ['CVC', conn.test_card.cvc], ['ZIP', conn.test_card.zip]].map(([k, v]) => (
              <div key={k} className="bg-gray-50 rounded-xl px-3 py-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{k}</p>
                <p className="text-sm font-mono font-semibold text-gray-900">{v}</p>
              </div>
            ))}
          </div>
        )}

        {test && (
          <div className="mt-5 border-t border-gray-100 pt-5" data-testid="eo-test-progress">
            <ol className="space-y-3">
              {[
                { done: true, label: 'Donation created in Altrunic', detail: test.donation_id },
                { done: true, label: 'Every.org checkout opened', detail: 'Complete the payment in the new tab' },
                { done: !!testStatus?.webhook_received, label: 'Every.org called our webhook',
                  detail: testStatus?.webhook_delivery
                    ? `charge ${testStatus.webhook_delivery.charge_id} · ${testStatus.webhook_delivery.payment_method || 'card'} · received ${new Date(testStatus.webhook_delivery.received_at).toLocaleTimeString()}`
                    : (testStatus?.donation?.checkout_exited_at
                        ? `Donor came back via the exit link at ${new Date(testStatus.donation.checkout_exited_at).toLocaleTimeString()} — the checkout was closed without paying, so there was nothing to notify us about`
                        : 'Waiting for the delivery…') },
                { done: !!testStatus?.paid, label: 'Donation marked paid and held by Every.org for granting',
                  detail: testStatus?.donation?.net_amount
                    ? `$${testStatus.donation.amount} gross · $${testStatus.donation.net_amount} net to the charity`
                    : 'Waiting for confirmation…' },
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`eo-test-step-${i}`}>
                  {step.done ? <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                             : <Loader2 className="w-5 h-5 text-gray-300 animate-spin flex-shrink-0" />}
                  <div>
                    <p className={`text-sm font-semibold ${step.done ? 'text-gray-900' : 'text-gray-400'}`}>{step.label}</p>
                    <p className="text-xs text-gray-500">{step.detail}</p>
                  </div>
                </li>
              ))}
            </ol>
            {testStatus?.paid && (
              <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <p className="text-sm font-bold text-emerald-900">End-to-end rail proven</p>
                <p className="text-xs text-emerald-800 mt-1">
                  A real Every.org charge reached this deployment and updated the donation. Receipts, campaign
                  totals and disbursement status all run off this same delivery.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <Radio className="w-4 h-4 text-blue-600" />
          <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">Webhook deliveries</h3>
        </div>
        <table className="w-full" data-testid="eo-deliveries-table">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Received</th>
              <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Charge</th>
              <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Nonprofit</th>
              <th className="text-right px-4 py-3 text-xs font-bold text-gray-500 uppercase">Amount</th>
              <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase">Env</th>
              <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase">Attempts</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {(conn?.recent_deliveries || []).map((d, i) => (
              <tr key={d.charge_id} className={`hover:bg-gray-50 ${d.origin !== 'external' ? 'opacity-50' : ''}`}
                data-testid={`eo-delivery-${i}`}>
                <td className="px-4 py-3 text-xs text-gray-600">{new Date(d.received_at).toLocaleString()}</td>
                <td className="px-4 py-3 text-xs font-mono text-gray-700">
                  {d.charge_id}
                  {d.origin !== 'external' && (
                    <span className="ml-2 px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 text-[10px] font-bold uppercase"
                      data-testid={`eo-delivery-synthetic-${i}`}>synthetic</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">{d.to_nonprofit?.name || '—'}</td>
                <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">
                  {d.amount !== null && d.amount !== undefined && Number.isFinite(Number(d.amount))
                    ? `$${Number(d.amount).toLocaleString()}` : '—'}
                </td>
                <td className="px-4 py-3 text-center text-xs uppercase font-bold text-gray-500">{d.environment}</td>
                <td className="px-4 py-3 text-center text-xs text-gray-500">{d.delivery_count || 1}</td>
              </tr>
            ))}
            {(conn?.recent_deliveries || []).length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center" data-testid="eo-no-deliveries">
                <CreditCard className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Every.org has never called this deployment</p>
                <p className="text-xs text-gray-400 mt-1">Register the callback above, then run the proof</p>
              </td></tr>
            )}
            {(conn?.recent_deliveries || []).length > 0 && !conn?.proven && (
              <tr><td colSpan={6} className="px-4 py-4 text-center bg-amber-50" data-testid="eo-all-synthetic-note">
                <p className="text-xs font-bold text-amber-900">
                  Every row above was generated by our own tooling. Every.org has still never called this deployment.
                </p>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Radio className="w-4 h-4 text-gray-500" />
            <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">Inbound requests to our webhook</h3>
          </div>
          <p className="text-xs text-gray-400">
            {conn?.inbound_hits_total || 0} total
            {conn?.inbound_hits_bad_secret ? ` · ${conn.inbound_hits_bad_secret} with a wrong secret` : ''}
          </p>
        </div>
        {(conn?.recent_hits || []).length === 0 ? (
          <div className="px-6 py-8 text-center" data-testid="eo-no-hits">
            <p className="text-sm text-gray-500">Nothing has ever POSTed to our webhook URL</p>
            <p className="text-xs text-gray-400 mt-1">
              If you've completed a donation and this is still empty, the webhook isn't registered on the
              {conn?.is_staging ? ' staging' : ' live'} dashboard (or that account's email isn't verified yet)
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50" data-testid="eo-hits-list">
            {conn.recent_hits.map((h, i) => (
              <div key={i} className="px-6 py-3 flex items-center gap-4 text-xs" data-testid={`eo-hit-${i}`}>
                {h.secret_ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                             : <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
                <span className="text-gray-600 w-44 flex-shrink-0">{new Date(h.received_at).toLocaleString()}</span>
                <span className="font-mono text-gray-700 flex-1 truncate">
                  {h.charge_id || 'no chargeId'} · {h.payload_keys?.length || 0} fields
                </span>
                <span className="text-gray-400 truncate max-w-[220px]">{h.user_agent || '—'}</span>
                <span className="uppercase font-bold text-gray-400">{h.environment}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EveryorgSetupView;
